import React, { useState, useEffect } from 'react';

interface RevenueLicenceData {
  numberPlate: string;
  revenueLicenceNumber: string;
  annualFee: number;
  arrears: number;
  fines: number;
  totalAmount: number;
  licenceStartDate: string;
  licenceEndDate: string;
  issuedDate: string;
}

interface FormErrors {
  [key: string]: string;
}

interface Config {}

interface IsLoading {
  config: boolean;
}

interface StepFiveProps {
  revenueLicenceData: RevenueLicenceData;
  setRevenueLicenceData: React.Dispatch<React.SetStateAction<RevenueLicenceData>>;
  revenueLicenceImages: File[];
  setRevenueLicenceImages: React.Dispatch<React.SetStateAction<File[]>>;
  formErrors: FormErrors;
  config: Config;
  isLoading: IsLoading;
  handleFileChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    maxSizeMB: number,
    allowedTypes: string[]
  ) => void;
}

const StepFive: React.FC<StepFiveProps> = ({
  revenueLicenceData,
  setRevenueLicenceData,
  revenueLicenceImages,
  setRevenueLicenceImages,
  formErrors,
  isLoading,
  handleFileChange,
}) => {
  const [revenueLicenceImagePreviews, setRevenueLicenceImagePreviews] = useState<string[]>([]);
  const maxFileSizeMB = 5;
  const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];

  // Generate image previews
  const generatePreviews = (files: File[], setPreviews: React.Dispatch<React.SetStateAction<string[]>>) => {
    const previews = files.map((file) => URL.createObjectURL(file));
    setPreviews((prev) => {
      prev.forEach(URL.revokeObjectURL);
      return previews;
    });
  };
  const todayStr=new Date().toISOString().split('T')[0];
  const clampToToday = (v:string)=>(v && v>todayStr ? todayStr : v||'')

  // Handle file removal
  const removeFile = (
    index: number,
    files: File[],
    setFiles: React.Dispatch<React.SetStateAction<File[]>>,
    previews: string[],
    setPreviews: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    const updatedPreviews = previews.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setPreviews(updatedPreviews);
    updatedPreviews.forEach(URL.revokeObjectURL);
  };

  // Update previews when files change
  useEffect(() => {
    generatePreviews(revenueLicenceImages, setRevenueLicenceImagePreviews);
    return () => revenueLicenceImagePreviews.forEach(URL.revokeObjectURL);
  }, [revenueLicenceImages]);

  // Clear images if numberPlate changes
  useEffect(() => {
    if (!revenueLicenceData.numberPlate) {
      setRevenueLicenceImages([]);
      setRevenueLicenceImagePreviews([]);
    }
  }, [revenueLicenceData.numberPlate, setRevenueLicenceImages]);

  // Calculate totalAmount when annualFee, arrears, or fines change
  useEffect(() => {
    const calculateTotalAmount = () => {
      const annualFee = revenueLicenceData.annualFee || 0;
      const arrears = revenueLicenceData.arrears || 0;
      const fines = revenueLicenceData.fines || 0;
      const total = annualFee + arrears + fines;
      setRevenueLicenceData((prev) => ({ ...prev, totalAmount: total }));
    };
    calculateTotalAmount();
  }, [revenueLicenceData.annualFee, revenueLicenceData.arrears, revenueLicenceData.fines, setRevenueLicenceData]);

  return (
    <div className="container">
      <div className="mb-4">
        <h5>Revenue Licence Records</h5>
        <div className="row g-4">
          {/* Left Column */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label mb-2">Number Plate</label>
              <input
                type="text"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={revenueLicenceData.numberPlate || ''}
                disabled
                placeholder="Number plate set in previous step"
              />
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Revenue Licence Number</label>
              <input
                type="text"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={revenueLicenceData.revenueLicenceNumber || ''}
                onChange={(e) => setRevenueLicenceData({ ...revenueLicenceData, revenueLicenceNumber: e.target.value })}
                placeholder="Enter revenue licence number"
                disabled={isLoading.config}
              />
              {formErrors.revenueLicenceNumber && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.revenueLicenceNumber}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Annual Fee</label>
              <input
                type="number"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={revenueLicenceData.annualFee || ''}
                onChange={(e) => setRevenueLicenceData({ ...revenueLicenceData, annualFee: parseFloat(e.target.value) || 0 })}
                placeholder="Enter annual fee"
                min="0"
                step="0.01"
                disabled={isLoading.config}
              />
              {formErrors.annualFee && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.annualFee}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Arrears</label>
              <input
                type="number"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={revenueLicenceData.arrears || ''}
                onChange={(e) => setRevenueLicenceData({ ...revenueLicenceData, arrears: parseFloat(e.target.value) || 0 })}
                placeholder="Enter arrears"
                min="0"
                step="0.01"
                disabled={isLoading.config}
              />
              {formErrors.arrears && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.arrears}
                </div>
              )}
            </div>
                        <div className="mb-3">
              <label className="col-form-label mb-2">Revenue Licence Images</label>
              <input
                type="file"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                multiple
                accept={allowedTypes.join(',')}
                onChange={(e) => handleFileChange(e, setRevenueLicenceImages, maxFileSizeMB, allowedTypes)}
                disabled={isLoading.config}
              />
              <small className="text-muted">Max {maxFileSizeMB}MB per file, formats: JPEG, PNG, JPG</small>
              {formErrors.revenueLicenceImages && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.revenueLicenceImages}
                </div>
              )}
              {revenueLicenceImages.length > 0 && (
                < div className="mt-3">
                  <h6>Selected Files:</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {revenueLicenceImages.map((file, index) => (
                      <div key={index} className="position-relative">
                        <img
                          src={revenueLicenceImagePreviews[index]}
                          alt={file.name}
                          style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm position-absolute top-0 end-0"
                          style={{ padding: '2px 6px' }}
                          onClick={() =>
                            removeFile(
                              index,
                              revenueLicenceImages,
                              setRevenueLicenceImages,
                              revenueLicenceImagePreviews,
                              setRevenueLicenceImagePreviews
                            )
                          }
                          disabled={isLoading.config}
                        >
                          ×
                        </button>
                        <div style={{ fontSize: '12px', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {file.name}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Right Column */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label mb-2">Fines</label>
              <input
                type="number"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={revenueLicenceData.fines || ''}
                onChange={(e) => setRevenueLicenceData({ ...revenueLicenceData, fines: parseFloat(e.target.value) || 0 })}
                placeholder="Enter fines"
                min="0"
                step="0.01"
                disabled={isLoading.config}
              />
              {formErrors.fines && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.fines}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Total Amount</label>
              <input
                type="number"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={revenueLicenceData.totalAmount || ''}
                readOnly
                placeholder="Calculated total amount"
                disabled
              />
              {formErrors.totalAmount && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.totalAmount}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Issued Date</label>
              <input
                type="date"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={revenueLicenceData.issuedDate || ''}
                max={todayStr}
                onChange={(e) => setRevenueLicenceData({ ...revenueLicenceData, issuedDate:clampToToday( e.target.value) })}
                disabled={isLoading.config}
              />
              {formErrors.issuedDate && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.issuedDate}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Licence Start Date</label>
              <input
                type="date"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={revenueLicenceData.licenceStartDate || ''}
                max={todayStr}
                onChange={(e) => setRevenueLicenceData({ ...revenueLicenceData, licenceStartDate:clampToToday (e.target.value) })}
                disabled={isLoading.config}
              />
              {formErrors.licenceStartDate && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.licenceStartDate}
                </div>
              )}
            </div>
            <div className="mb-3">
              <label className="col-form-label mb-2">Licence End Date</label>
              <input
                type="date"
                className="form-control"
                style={{ width: '100%', padding: '10px 15px' }}
                value={revenueLicenceData.licenceEndDate || ''}
                onChange={(e) => setRevenueLicenceData({ ...revenueLicenceData, licenceEndDate: e.target.value })}
                disabled={isLoading.config}
              />
              {formErrors.licenceEndDate && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '8px' }}>
                  {formErrors.licenceEndDate}
                </div>
              )}
            </div>


          </div>
        </div>
      </div>
    </div>
  );
};

export default StepFive;